import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/hooks/useAuth';
import {
  Briefcase,
  Building2,
  Users,
  Globe,
  LayoutDashboard as PageIcon,
  Loader2,
  Eye,
  Settings,
  PlusCircle,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/atoms/button';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

// KPI Item - V4 - Inspired by onboarding step list item
const KpiItem = ({
  title,
  value,
  icon: Icon,
  isLoading,
  description,
  iconContainerClassName = 'bg-terrarium-orange/10',
  iconClassName = 'text-terrarium-orange',
}: {
  title: string;
  value?: number | string;
  icon: React.ElementType;
  isLoading?: boolean;
  description?: string;
  iconContainerClassName?: string;
  iconClassName?: string;
}) => {
  return (
    <div className="flex items-start p-4 bg-white rounded-lg shadow-sm border border-slate-200/60">
      <div
        className={`flex-shrink-0 h-10 w-10 rounded-full ${iconContainerClassName} flex items-center justify-center mr-4`}
      >
        <Icon className={`h-5 w-5 ${iconClassName}`} />
      </div>
      <div>
        <h3 className="font-body text-sm font-semibold text-slate-700 mb-0.5">
          {title}
        </h3>
        {isLoading ? (
          <div className="h-7 mt-1">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
          </div>
        ) : (
          <p className="font-body text-2xl font-semibold text-slate-800">
            {value !== undefined ? value : '-'}
          </p>
        )}
        {description && !isLoading && (
          <p className="font-body text-xs text-slate-500 mt-1">{description}</p>
        )}
      </div>
    </div>
  );
};

export function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();

  // Fetch platform statistics using admin function
  const {
    data: platformStats,
    isLoading: isLoadingStats,
    error: overallError,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ['platformStats'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_platform_stats_admin');
      if (error)
        throw new Error(`Failed to fetch platform stats: ${error.message}`);
      return data;
    },
    enabled: true,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  const quickActions = [
    {
      label: 'Manage Communities',
      href: '/admin/communities',
      icon: Globe,
      primary: true,
    },
    { label: 'Platform Jobs', href: '/admin/jobs', icon: Briefcase },
    { label: 'All Employers', href: '/admin/employers', icon: Building2 },
    { label: 'User Management', href: '/admin/users', icon: Users },
    {
      label: 'Platform Settings',
      href: '/admin/settings?settings_section=platform',
      icon: Settings,
    },
  ];

  // Helper function to get the best name to display
  const getUserDisplayName = () => {
    if (!user) return '';

    // Priority: first_name > full_name > email prefix
    const firstName = user.user_metadata?.first_name;
    const fullName = user.user_metadata?.full_name;
    const email = user.email;

    if (firstName && firstName.trim()) {
      return firstName.trim();
    }

    if (fullName && fullName.trim()) {
      return fullName.trim();
    }

    if (email) {
      return email.split('@')[0];
    }

    return 'Admin';
  };

  if (isLoadingStats) {
    return (
      <div className="font-body flex flex-1 items-center justify-center bg-white">
        <Loader2 className="h-10 w-10 animate-spin text-terrarium-orange" />
      </div>
    );
  }

  if (overallError) {
    return (
      <div className="font-body flex flex-col flex-1 items-center justify-center p-10 text-center bg-white">
        <PageIcon className="h-12 w-12 text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-red-700">
          Error Loading Dashboard
        </h2>
        <p className="text-slate-500 mb-6">{overallError.message}</p>
        <Button onClick={() => refetchStats()} variant="outline">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="font-body flex flex-col flex-1 bg-white p-6 sm:p-8 md:p-12 space-y-10 md:space-y-12">
      {/* Welcome Section */}
      {user && !authLoading && (
        <section className="text-center md:text-left">
          <h1 className="text-4xl sm:text-5xl font-normal tracking-tight text-slate-800 font-perfectly-nineties">
            Welcome back,{' '}
            <span className="text-terrarium-orange">
              {getUserDisplayName()}!
            </span>
          </h1>
          <p className="mt-3 text-md sm:text-lg text-slate-600 max-w-xl mx-auto md:mx-0">
            Platform administration dashboard for Terrarium. Monitor
            communities, jobs, and platform health.
          </p>
        </section>
      )}

      {/* KPI Stats Section - Minimalist List/Grid Style */}
      <section>
        <div className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <KpiItem
            title="Total Communities"
            value={platformStats?.total_communities ?? undefined}
            icon={Globe}
            isLoading={isLoadingStats}
            description="Active communities on the platform."
            iconContainerClassName="bg-blue-50"
            iconClassName="text-blue-600"
          />
          <KpiItem
            title="Active Jobs"
            value={platformStats?.active_jobs ?? undefined}
            icon={Briefcase}
            isLoading={isLoadingStats}
            description="Jobs currently open for applications."
            iconContainerClassName="bg-green-50"
            iconClassName="text-green-600"
          />
          <KpiItem
            title="Total Employers"
            value={platformStats?.total_employers ?? undefined}
            icon={Building2}
            isLoading={isLoadingStats}
            description="Employers across all communities."
            iconContainerClassName="bg-purple-50"
            iconClassName="text-purple-600"
          />
          <KpiItem
            title="Platform Users"
            value={platformStats?.total_users ?? undefined}
            icon={Users}
            isLoading={isLoadingStats}
            description="Total registered users."
            iconContainerClassName="bg-amber-50"
            iconClassName="text-amber-600"
          />
        </div>
      </section>

      {/* Quick Actions Section */}
      <section>
        <div className="space-y-3">
          <h2 className="font-perfectly-nineties text-2xl text-slate-700">
            Quick Actions
          </h2>
          <div className="flex flex-wrap gap-3 sm:gap-4">
            {quickActions.map((action) => (
              <Link
                key={action.label}
                to={action.href}
                className={cn(
                  'flex items-center space-x-2.5 py-2.5 px-4 border rounded-lg text-xs sm:text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2',
                  action.primary
                    ? 'bg-terrarium-orange hover:bg-terrarium-orange/90 text-white border-transparent shadow-sm focus:ring-terrarium-orange/70'
                    : 'bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-800 border-slate-300 focus:ring-indigo-500'
                )}
              >
                <action.icon
                  className={cn(
                    'h-4 w-4',
                    action.primary ? 'text-white/90' : 'text-slate-500'
                  )}
                />
                <span>{action.label}</span>
                {action.primary && (
                  <ArrowRight className="h-4 w-4 text-white/90" />
                )}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Analytics Placeholder */}
      <section>
        <div className="bg-slate-50/80 rounded-xl p-6 sm:p-8 border border-slate-200/70">
          <h2 className="font-perfectly-nineties text-2xl text-slate-700 mb-2">
            Platform Analytics
          </h2>
          <p className="font-body text-sm text-slate-500 mb-6">
            Comprehensive insights into platform performance and usage.
          </p>
          <div className="text-center text-slate-500 py-16 sm:py-20 bg-white rounded-lg border border-dashed border-slate-300">
            <PageIcon className="h-10 w-10 sm:h-12 sm:w-12 text-slate-300 mx-auto mb-3" />
            <p className="font-body text-md font-medium mb-1">
              Advanced Analytics Coming Soon
            </p>
            <p className="font-body text-xs sm:text-sm text-slate-400 max-w-xs mx-auto">
              We're developing comprehensive platform analytics and reporting.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
